import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useRouteError } from 'react-router';

import { APPS } from '@cccom/config/app-config';
import { useEnvironment } from '@cccom/config/env';
import { log } from '@cccom/shared/logging';
import { Button } from '@cccom/ui/src/components/shadcn/button';

import InlineSvgNoInternet from './InlineSvgNoInternet';

export type CCErrorPageProps = {
  type?:
    | '404'
    | 'PAGE_ERROR'
    | 'NO_INTERNET'
    | 'UNAUTHORIZED'
    | 'SOMETHING_WENT_WRONG';
};

export type ErrorLabelType = {
  headerText?: string;
  mainText?: string;
  buttonText?: string;
  canNavigate?: boolean;
};

function SomethingWentWrong() {
  const [t] = useTranslation();

  return (
    <div className="container p-4 pt-24 text-gray-600">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="flex flex-col items-center justify-center gap-4">
          <img
            src="images/error-boundary.png"
            alt="something-went-wrong"
            className="max-h-[100px]"
          />
          <h2 className="text-left text-2xl leading-none">
            {t('common.errors.something_went_wrong')}
          </h2>
          <p className="w-[400px] p-0 text-center text-base">
            <Trans i18nKey="common.errors.something_went_wrong_blurb" />
          </p>
        </div>
      </div>
    </div>
  );
}

export function CCErrorPage({ type }: CCErrorPageProps) {
  const error = useRouteError();
  const env = useEnvironment();
  const [t] = useTranslation();

  useEffect(() => {
    if (error) log.error('Unexpected Error:', error);
  }, [error]);

  let errorLabels: ErrorLabelType;

  switch (type) {
    case '404':
      errorLabels = {
        headerText: t('common.errors.page_not_found'),
        mainText: t('common.errors.sorry_page_moved'),
        buttonText: t('common.actions.404_button'),
        canNavigate: true,
      };
      break;
    case 'PAGE_ERROR':
      errorLabels = {
        headerText: t('common.errors.something_went_wrong'),
        mainText: t('common.errors.technical_difficulties'),
        buttonText: t('common.actions.try_again'),
        canNavigate: false,
      };
      break;
    case 'NO_INTERNET':
      errorLabels = {
        headerText: t('common.errors.no_internet_connection'),
        mainText: t('common.errors.no_internet_msg'),
        buttonText: t('common.actions.try_again'),
        canNavigate: false,
      };
      break;
    case 'UNAUTHORIZED':
      errorLabels = {
        headerText: t('common.errors.unauthorized'),
        buttonText: t('common.actions.404_button'),
        canNavigate: true,
      };
      break;
    case 'SOMETHING_WENT_WRONG': {
      return <SomethingWentWrong />;
    }
    default:
      errorLabels = {
        headerText: t('common.errors.something_went_wrong'),
        mainText: t('common.errors.technical_difficulties'),
        buttonText: t('common.actions.404_button'),
        canNavigate: true,
      };
      break;
  }

  const { headerText, mainText, buttonText, canNavigate } = errorLabels;

  // TODO: Extract the no internet case to a warning toast components instead of a full page blocking UI

  return (
    <div className="container p-4 pt-24">
      <div className="flex items-center justify-center">
        <div className="flex max-w-[50%] flex-col items-start gap-4 pr-20">
          <h1 className="text-left text-6xl font-semibold text-blue-950">
            {headerText}
          </h1>
          <p className="w-[400px] py-8 text-left text-xl text-gray-600">
            {mainText}
          </p>

          {buttonText &&
            (canNavigate && env.APP?.path ? (
              <a href={APPS.getUrl(env, env.APP)}>
                <Button variant="default">{buttonText}</Button>
              </a>
            ) : (
              type !== '404' && (
                <Button
                  variant="default"
                  onClick={() => window.location.reload()}
                >
                  {buttonText}
                </Button>
              )
            ))}
        </div>
        <div className="flex flex-col items-center justify-center p-4">
          {type === 'NO_INTERNET' ? (
            <InlineSvgNoInternet />
          ) : (
            <img
              src={env.APP?.errorLogo ?? 'images/cc-404-graphic.svg'}
              alt="logo-graphic"
              className="max-w-full"
            />
          )}
        </div>
      </div>
    </div>
  );
}
